import axios from 'axios'
import OAuth from '../OAuth'
import { EventBus } from '@/utils/Global'

let oauth = null

// axios.defaults.withCredentials = true
axios.defaults.headers = {
  'X-Requested-With': 'XMLHttpRequest'
}

export const ROOT_URL = process.env.API_URL

function isInvalidOAuthToken (err) {
  return err.response && err.response.data && err.response.data.error === 'invalid_token'
}

export function request (options) {
  return axios
    .request(options)
    .catch(err => {
      if (isInvalidOAuthToken(err)) {
        return OAuth
          .refreshToken(oauth.refresh_token)
          .then(token => {
            setOAuthToken(token)
            return request(options)
          })
          .catch(() => {
            setOAuthToken(null)
            EventBus.$emit('auth-failed')
          })
      } else {
        // Nothing to see here, pass it on
        throw err
      }
    })
}

/**
 * Because the backend returns FULL URL, instead of relative url, we need to override it.
 *
 * @param {string} url
 * @returns {string} url relative to our root url
 */
function fixProxyUrl (url) {
  if (url.indexOf('http://') < 0 &&
    url.indexOf('https://') < 0 &&
    url.charAt(0) !== '/') {
    return `${ROOT_URL}/${url}`
  } else {
    let match = /\/api\/(.*)/.exec(url)
    if (match && match.length) {
      return `${ROOT_URL}/${match[1]}`
    }
    return url
  }
}

export function setOAuthToken (token) {
  oauth = token
  axios.defaults.headers = token
    ? {
      'Authorization': `${token.token_type} ${token.access_token}`
    }
    : null
}

function fixLinkUrl (url) {
  return url.replace(/\{[^}]*\}/g, '')
}

export function fetch (url, name, options) {
  url = fixLinkUrl(url)
  options = options || {}

  options.fetchLinks = options.fetchLinks
  if (options.fetchLinks === undefined) {
    options.fetchLinks = !!options.include
  }

  url = fixProxyUrl(url)

  let params = {}
  if (options.filters) {
    for (let i in options.filters) {
      params[i] = options.filters[i]
    }
  }

  if (options.sort) {
    params.sort = options.sort
  }

  if (options.projection) {
    params.projection = options.projection
  }

  params.size = 999999

  if (options.size !== undefined) {
    params.size = options.size
  }

  if (typeof options.size !== 'number') {
    delete params.size
  }

  if (options.page) {
    params.page = options.page
  }

  return request(
    {
      method: 'GET',
      url,
      params
    })
    .then(result => {
      if (result.data._embedded === undefined || result.data.enabled !== undefined) {
        let item = result.data
        if (!item._links) {
          item._links = {
            self: {
              href: url
            }
          }
        }

        postProcess(item)

        if (options.fetchLinks) {
          return fetchLinks(item, name, options)
            .then(() => item)
        } else {
          return item
        }
      }

      let info = {
        items: result.data._embedded[Object.keys(result.data._embedded).pop()],
        _page: result.data.page || {}
      }

      options.include = options.include || null

      info.items.forEach(item => {
        postProcess(item)
      })

      if (options.fetchLinks) {
        return Promise.all(
          info.items.map(item => fetchLinks(item, name, options))
        ).then(() => {
          return info
        })
      } else {
        return info
      }
    })
}

function fetchLinks (item, name, options) {
  if (!item._sources) {
    item._sources = {}
  }

  let links = options.include || Object.keys(item._links).filter(item => item !== 'self' && item !== name && item !== name.substr(0, name.length - 1))
  return Promise.all(
    links
      .map(field => {
        let subInclude

        // Alternative format allowing us to fetch sublinks
        if (typeof field !== 'string') {
          subInclude = field.slice(1)
          field = field[0]
        }

        return fetch(item._links[field].href, field, { size: null, include: subInclude })
          .then(result => {
            if (result._page !== undefined) {
              item[field] = result.items
            } else {
              item[field] = result
            }
            item._sources[field] = item._links[field].href
          })
          .catch(err => {
            if (err && err.response && err.response.status !== 404) {
              console.error(err)
              console.log('Failed to fetch', field)
            }

            item[field] = null
            item._sources[field] = item._links[field].href
          })
      })
  )
}

/**
 * Returns relations contained in model data in format of:
 *  relation_url = relation_item_url[]
 *
 * @param {Object} data model data
 * @return {{[key: string]: string}} relations
 */
function splitRelations (data, options) {
  let relations = {}
  options = options || {}

  Object.keys(data).forEach(key => {
    let value = data[key]
    let source = (data && data._sources && data._sources[key]) || `${data._self}/${key}`

    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item._self !== undefined) {
          if (!source) {
            console.warn('[API]', 'Saving of relation', key, 'is not possible because relation source is missing.')
          } else {
            if (!relations[source]) relations[source] = []
            relations[source].push(item._self)
          }
        }
      })
    } else if (value && value._self !== undefined) {
      if (!source) {
        console.warn('[API]', 'Saving of relation', key, 'is not possible because relation source is missing.')
      } else {
        relations[source] = [value._self]
      }
    }
  })

  return relations
}

/**
 * Applies relations contained in model data.
 * @param {Object} data model data
 */
export function applyRelations (data, options) {
  options = options || {}
  let relations = splitRelations(data, options)
  return Promise.all(
    Object.keys(relations)
      .map(key => {
        let value = relations[key].filter(item => !!item)
        if (value.length > 0) {
          return request({
            method: 'PUT',
            url: key,
            data: value.join('\r\n'),
            headers: {
              'Content-Type': 'text/uri-list'
            }
          })
        } else {
          return request({
            method: 'DELETE',
            url: key
          })
        }
      })
  )
}

function getDataOnly (data) {
  let updated = {}
  Object.keys(data).forEach(field => {
    if (field.substring(0, 1) !== '_' &&
      (!data._sources || data._sources[field] === undefined) &&
      !Array.isArray(data[field]) &&
      !typeof data[field] !== 'function' &&
      (data[field] !== undefined && data[field] !== null && (typeof data[field] !== 'object' || data[field]._self === undefined))
    ) {
      updated[field] = data[field]
    }
  })
  return updated
}

export function fetchLink (item, link, options) {
  return fetch(item._links[link].href, link, options)
}

export function postProcess (item) {
  Object.keys(item._links).forEach(key => {
    let link = item._links[key]
    link.href = fixProxyUrl(link.href).replace('{?projection}', '')
  })

  item._self = item._links.self.href
  item._sources = {}
  item.id = item._self.split('/').filter(item => item.length > 0).pop()
  item.fetch = (link, options) => fetchLink(item, link, options)

  return item
}

export default (options) => {
  let name = options.name
  let enums = options.enums || {}
  let methods = options.methods || {}

  let model = {
    name,
    enums,

    getList (options) {
      return fetch(name, name, options)
    },

    create (data) {
      // Copy data, and skip _links and _self
      let updated = getDataOnly(data)

      return request(
        {
          method: 'POST',
          url: `${ROOT_URL}/${name}`,
          data: updated
        })
        .then(response => {
          data._links = response.data._links
          postProcess(data)
          return applyRelations(data, options)
        })
        .then(() => {
          return data
        })
    },

    update (data, fields) {
      data = Object.keys(data)
        .filter(field => !fields || field.charAt(0) === '_' || fields.indexOf(field) >= 0)
        .reduce((result, field) => {
          result[field] = data[field]
          return result
        }, {})

      return applyRelations(data, options).then(() => {
        // Copy data, and skip _links and _self
        let updated = getDataOnly(data)

        return request(
          {
            method: 'PATCH',
            url: data._links.self.href,
            data: updated
          })
          .then(result => {
            return postProcess(result.data)
          })
      })
    },

    updateRelation (data, field) {
      let updated = {
        _links: data._links,
        _self: data._self,
        _sources: data._sources,
        [field]: data[field]
      }
      return applyRelations(updated, {})
    },

    delete (data) {
      let updated = getDataOnly(data)
      updated.deleted = true
      return request(
        {
          method: 'PUT',
          url: data._links.self.href,
          data: updated
        })
    },

    fetch,
    applyRelations,
    postProcess,
    fetchLink,
    ROOT_URL
  }

  Object.keys(methods).forEach(key => {
    model[key] = methods[key].bind(model)
  })

  return model
}



// WEBPACK FOOTER //
// ./src/models/factory/Siprec.js